/**
 * BulkTriageBar · Phase A · Step 4 + Step 5 Coach-Accelerator.
 *
 * Two paired actions in one bar so the coach can clear the triage
 * board in seconds:
 *
 *   • ✓ Accept All ≥ X%   — flips unverified vectors with conf ≥ X to
 *                            verified AND queues the auto-harvest
 *                            pipeline (FFmpeg slice + B2 upload) per
 *                            newly-verified vector.
 *   • ✗ Reject All < Y%   — flips low-confidence unverified noise to
 *                            rejected. NO harvest is queued (rejected
 *                            vectors are explicitly excluded from the
 *                            Phase-B training corpus).
 *
 * Render contract:
 *   • Hidden when no unverified vectors exist (parent gates it).
 *   • Each slider has its own range:
 *       Accept: [0.60, 0.95], default 0.85
 *       Reject: [0.30, 0.70], default 0.55
 *     Distinct ranges enforce a visual + UX gap between the two
 *     decisions so the coach never confuses them.
 *   • Live counters recompute on every drag tick.
 *   • Both buttons are disabled when their eligibility count is 0.
 *   • Confirms via `window.confirm` before firing — destructive-adjacent
 *     actions that flip status + potentially queue N background jobs.
 *
 * Backend wires:
 *   • POST /api/clips/{id}/pass-vectors/bulk-verify {min_confidence}
 *   • POST /api/clips/{id}/pass-vectors/bulk-reject {max_confidence}
 */
import React from "react";
import { CheckCircle2, Loader2, Rocket, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";

export function BulkTriageBar({
  // Bulk-Accept controls
  acceptThreshold, onAcceptThresholdChange,
  acceptEligibleCount,
  // Bulk-Reject controls
  rejectThreshold, onRejectThresholdChange,
  rejectEligibleCount,
  // Shared
  unverifiedTotal,
  onBulkVerify, onBulkReject,
  busy,
}) {
  const acceptPct = Math.round(acceptThreshold * 100);
  const rejectPct = Math.round(rejectThreshold * 100);
  const acceptDisabled = busy || acceptEligibleCount === 0;
  const rejectDisabled = busy || rejectEligibleCount === 0;

  return (
    <div
      data-testid="pass-vector-bulk-bar"
      className="border-b border-border bg-emerald-400/[0.03]"
    >
      {/* Top row — bulk ACCEPT */}
      <BulkRow
        rowKind="accept"
        icon={<Rocket className="w-3.5 h-3.5 text-emerald-300" />}
        label="Bulk-Accept"
        labelTone="text-emerald-200"
        thumbAccent="emerald"
        slider={
          <Slider
            data-testid="bulk-threshold-slider"
            value={[acceptThreshold]}
            onValueChange={(v) => onAcceptThresholdChange(Array.isArray(v) ? v[0] : v)}
            min={0.6}
            max={0.95}
            step={0.01}
            disabled={busy}
            className="flex-1 max-w-[260px]"
          />
        }
        readout={(
          <Badge
            data-testid="bulk-threshold-readout"
            className="bg-emerald-400/15 text-emerald-300 border-emerald-400/40 border font-mono text-[10px] tabular-nums uppercase tracking-widest"
          >
            ≥ {acceptPct}%
          </Badge>
        )}
        counter={(
          <div
            data-testid="bulk-eligible-counter"
            className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground tabular-nums shrink-0"
          >
            <span className="text-emerald-300 font-bold">{acceptEligibleCount}</span>
            {" / "}
            <span>{unverifiedTotal}</span>
            {" "}of unverified will be accepted
          </div>
        )}
        button={(
          <Button
            data-testid="bulk-verify-btn"
            size="sm"
            onClick={onBulkVerify}
            disabled={acceptDisabled}
            className="h-7 px-3 bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400 hover:text-black border border-emerald-400/40 font-display font-bold uppercase tracking-widest text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
            title={
              acceptEligibleCount === 0
                ? "No unverified vectors meet the current threshold"
                : `Accept ${acceptEligibleCount} vector(s) and auto-harvest each`
            }
          >
            {busy ? (
              <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Accepting…</>
            ) : (
              <><CheckCircle2 className="w-3 h-3 mr-1" /> Accept All ≥ {acceptPct}%</>
            )}
          </Button>
        )}
      />

      {/* Bottom row — bulk REJECT */}
      <BulkRow
        rowKind="reject"
        icon={<X className="w-3.5 h-3.5 text-rose-300" />}
        label="Bulk-Reject"
        labelTone="text-rose-200"
        thumbAccent="rose"
        slider={
          <Slider
            data-testid="bulk-reject-threshold-slider"
            value={[rejectThreshold]}
            onValueChange={(v) => onRejectThresholdChange(Array.isArray(v) ? v[0] : v)}
            min={0.3}
            max={0.7}
            step={0.01}
            disabled={busy}
            className="flex-1 max-w-[260px]"
          />
        }
        readout={(
          <Badge
            data-testid="bulk-reject-threshold-readout"
            className="bg-rose-400/15 text-rose-300 border-rose-400/40 border font-mono text-[10px] tabular-nums uppercase tracking-widest"
          >
            &lt; {rejectPct}%
          </Badge>
        )}
        counter={(
          <div
            data-testid="bulk-reject-eligible-counter"
            className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground tabular-nums shrink-0"
          >
            <span className="text-rose-300 font-bold">{rejectEligibleCount}</span>
            {" / "}
            <span>{unverifiedTotal}</span>
            {" "}of unverified will be rejected
          </div>
        )}
        button={(
          <Button
            data-testid="bulk-reject-btn"
            size="sm"
            onClick={onBulkReject}
            disabled={rejectDisabled}
            className="h-7 px-3 bg-rose-400/15 text-rose-300 hover:bg-rose-400 hover:text-black border border-rose-400/40 font-display font-bold uppercase tracking-widest text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
            title={
              rejectEligibleCount === 0
                ? "No unverified vectors fall below the current threshold"
                : `Reject ${rejectEligibleCount} low-confidence vector(s) (no harvest)`
            }
          >
            {busy ? (
              <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Rejecting…</>
            ) : (
              <><X className="w-3 h-3 mr-1" /> Reject All &lt; {rejectPct}%</>
            )}
          </Button>
        )}
      />
    </div>
  );
}

/**
 * BulkRow · shared layout chrome for the two bulk actions.
 * Keeps the slider, readout, counter, and action button in a single
 * flex line so the coach reads each row left-to-right exactly the
 * same way.
 */
function BulkRow({ rowKind, icon, label, labelTone, slider, readout, counter, button }) {
  return (
    <div
      data-testid={`bulk-row-${rowKind}`}
      className="flex flex-wrap items-center gap-3 px-3 py-2.5 first:border-b first:border-border/40"
    >
      <div className="flex items-center gap-2 shrink-0 w-[112px]">
        {icon}
        <span className={`font-mono text-[10px] uppercase tracking-widest ${labelTone}`}>
          {label}
        </span>
      </div>

      <div className="flex items-center gap-3 flex-1 min-w-[220px]">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
          Threshold
        </span>
        {slider}
        {readout}
      </div>

      {counter}
      {button}
    </div>
  );
}
